import React, {
  useState,
  useEffect
} from 'react';

import { Form, Field } from "react-final-form";

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  CircularProgress,
  Grid,
  Typography,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  InputLabel,
  MenuItem,
  ListSubheader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  colors,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

import {
  ObservableAPI,
} from '../../../data/ObservableAPI';
import {
  ThesisUGBReview
} from '../../../types';

import localization from '../../../utils/localizations';


const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  mainGrid: {
    padding: 8,
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  gridLabel: {
    textAlign: 'right',
    color: '#555555',
  },
  alignRight: {
    textAlign: 'right',
  },
  form: {
    marginRight: 30,
  }
}));

function ThesisUGBGradingReviewViewer(props: {
  reviewId: string;
  reviewerName?: string;
}) {
  const classes = useStyles();
  const [review, setReview] = useState(undefined as ThesisUGBReview|undefined);
  const [showForm, setShowForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadReview = async (id: string) => {
    if (isSaving) return;

    const api = new ObservableAPI();
    setIsLoading(true);
    try {
      const result = await api.get(`thesis-ugb-reviews/${id}/`);
      if (result && (result as any).id) setReview(result as ThesisUGBReview);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadReview(props.reviewId);
  }, [props.reviewId]);

  if (!isLoading && review?.status === 'final') {
    return (<ThesisUGBReviewViev review={review} reviewerName={props.reviewerName} />);
  }
  return <></>;
}

function ThesisUGBReviewViev(props: {
  review: ThesisUGBReview,
  reviewerName?: string;
}) {
  const classes = useStyles();

  let title = localization.ugbReview;
  if (props.reviewerName) {
    title = `${title} - ${props.reviewerName}`
  }

  return (
    <Paper className={classes.mainBody}>
      <ListSubheader style={{paddingLeft: 8}}>{title}</ListSubheader>
      <List dense>
        <ListItem>
          <ListItemIcon>
            {(props.review.content_fitness === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.content_fitness === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Isi UGB sesuai dengan jalur pilihan"
            secondary={props.review.content_fitness === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.content_fitness_comment}</span> : null}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {(props.review.originality === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.originality === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Topik UGB belum pernah diteliti oleh mahasiswa S2 TL ITB"
            secondary={props.review.originality === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.originality_comment}</span> : null}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {(props.review.concise_title === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.concise_title === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Judul tepat, singkat dan jelas"
            secondary={props.review.concise_title === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.concise_title_comment}</span> : null}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {(props.review.clear_background === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.clear_background === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Latar belakang permasalahan jelas"
            secondary={props.review.clear_background === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.clear_background_comment}</span> : null}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {(props.review.clear_problem_identification === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.clear_problem_identification === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Identifikasi masalah jelas"
            secondary={props.review.clear_problem_identification === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.clear_problem_identification_comment}</span> : null}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {(props.review.clear_research_purpose === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.clear_research_purpose === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Tujuan penelitian jelas"
            secondary={props.review.clear_research_purpose === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.clear_research_purpose_comment}</span> : null}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {(props.review.methodology_fit_with_purpose === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.methodology_fit_with_purpose === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Metode dan rancangan penelitian sesuai dengan tujuan penelitian"
            secondary={props.review.methodology_fit_with_purpose === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.methodology_fit_with_purpose_comment}</span> : null}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {(props.review.methodology_clear_procedure === true) && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.methodology_clear_procedure === false) && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Prosedur penelitian diuraikan secara jelas"
            secondary={props.review.methodology_clear_procedure === false ? <span style={{whiteSpace: 'pre-line'}}>{props.review.methodology_clear_procedure_comment}</span> : null}
          />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            {(props.review.reccommendation === 'approved') && <CheckIcon style={{ color: colors.green[500] }} />}
            {(props.review.reccommendation === 'approved_with_revision') && <CheckIcon style={{ color: colors.orange[500] }} />}
            {(props.review.reccommendation === 'presentation_required') && <CheckIcon style={{ color: colors.orange[500] }} />}
            {(props.review.reccommendation === 'rejected') && <CloseIcon style={{ color: colors.red[500] }} />}
          </ListItemIcon>
          <ListItemText
            primary="Rekomendasi Reviewer"
            secondary={<div>{props.review.reccommendation === 'approved' ? <b style={{color: 'green'}}>Disetujui, tanpa perbaikan</b> : ''}
            {props.review.reccommendation === 'approved_with_revision' ? <b style={{color: 'orange'}}>Disetujui, dengan perbaikan</b> : ''}
            {props.review.reccommendation === 'presentation_required' ? <b style={{color: 'orange'}}>Perlu diperjelas dengan presentasi</b> : ''}
            {props.review.reccommendation === 'rejected' ? <b style={{color: 'red'}}>Tidak disetujui, harus mengganti judul penelitian</b> : ''}</div>}
          />
        </ListItem>
      </List>
    </Paper>
  );
}

export {ThesisUGBGradingReviewViewer};